/**
 * ErrorBoundary - Phase 4.1 of the CJM Overhaul
 *
 * Previously this component silently caught errors and re-rendered children,
 * defeating the entire purpose of an error boundary. Now it:
 *   - Logs errors to console (dev) and to /api/client-errors (prod, best-effort)
 *   - Renders a user-facing fallback UI with "Try again" and "Go home" actions
 *   - Accepts an optional `fallback` prop (function or React node) for custom UI
 *   - Exposes a `resetKeys` prop that resets the boundary when deps change
 */

import React, { Component } from "react";

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
    this.handleReset = this.handleReset.bind(this);
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    // Always log in dev; best-effort report in prod.
    try {
      // eslint-disable-next-line no-console
      console.error("[ErrorBoundary]", error, errorInfo);
    } catch (_) {}
    // Best-effort async report; never throw from here.
    try {
      if (typeof window !== "undefined" && window.fetch) {
        window.fetch("/api/v1/client-errors", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            message: error?.message || String(error),
            stack: error?.stack || null,
            componentStack: errorInfo?.componentStack || null,
            url: window.location.href,
            userAgent: navigator.userAgent,
            time: new Date().toISOString(),
          }),
          keepalive: true,
        }).catch(() => {});
      }
    } catch (_) {}
  }

  componentDidUpdate(prevProps) {
    const prev = prevProps.resetKeys || [];
    const curr = this.props.resetKeys || [];
    const changed =
      prev.length !== curr.length || prev.some((v, i) => v !== curr[i]);
    if (this.state.hasError && changed) {
      // Reset when parent tells us the surrounding context has changed
      this.setState({ hasError: false, error: null, errorInfo: null });
    }
  }

  handleReset() {
    this.setState({ hasError: false, error: null, errorInfo: null });
    if (typeof this.props.onReset === "function") {
      try { this.props.onReset(); } catch (_) {}
    }
  }

  render() {
    if (this.state.hasError) {
      if (typeof this.props.fallback === "function") {
        return this.props.fallback({
          error: this.state.error,
          reset: this.handleReset,
        });
      }
      if (this.props.fallback) {
        return this.props.fallback;
      }
      return (
        <div
          role="alert"
          style={{
            padding: "48px 24px",
            maxWidth: 640,
            margin: "80px auto",
            textAlign: "center",
            fontFamily: "system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
          }}
        >
          <h2 style={{ margin: "0 0 12px", color: "#1f2937" }}>
            Something went wrong
          </h2>
          <p style={{ color: "#6b7280", margin: "0 0 24px" }}>
            An unexpected error occurred. The team has been notified.
          </p>
          {process.env.NODE_ENV !== "production" && this.state.error && (
            <pre
              style={{
                textAlign: "left",
                background: "#f9fafb",
                border: "1px solid #e5e7eb",
                borderRadius: 8,
                padding: 12,
                overflow: "auto",
                fontSize: 12,
                color: "#b91c1c",
              }}
            >
              {this.state.error.message}
              {"\n"}
              {this.state.error.stack}
            </pre>
          )}
          <div style={{ display: "flex", gap: 12, justifyContent: "center", marginTop: 16 }}>
            <button
              type="button"
              onClick={this.handleReset}
              style={{
                padding: "10px 20px",
                borderRadius: 6,
                border: "none",
                background: "#2563eb",
                color: "#fff",
                cursor: "pointer",
                fontSize: 14,
                fontWeight: 500,
              }}
            >
              Try again
            </button>
            <button
              type="button"
              onClick={() => {
                // SPA-friendly nav: change the URL without a full reload, then
                // clear the error state so the new route can render.
                if (typeof window !== "undefined" && window.location.pathname !== "/") {
                  window.history.pushState({}, "", "/");
                  window.dispatchEvent(new PopStateEvent("popstate"));
                }
                this.setState({ hasError: false, error: null });
              }}
              style={{
                padding: "10px 20px",
                borderRadius: 6,
                border: "1px solid #d1d5db",
                background: "#fff",
                color: "#374151",
                cursor: "pointer",
                fontSize: 14,
                fontWeight: 500,
              }}
            >
              Go home
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
